import { useState, type ComponentProps } from 'react'
import { Platform, useWindowDimensions, type StyleProp, type ViewStyle } from 'react-native'
import { SharedValue, useSharedValue } from 'react-native-reanimated'
import { useAnimatedStyle } from 'react-native-reanimated'
import { useSafeAreaFrame, useSafeAreaInsets } from 'react-native-safe-area-context'
import { useIsCardActivityFeedBottomSheetFullHeight } from './CardActivityFeedBottomSheet'

const handleHeight = 36 satisfies Extract<
  (ComponentProps<typeof import('@gorhom/bottom-sheet').default>['handleStyle'] extends StyleProp<
    infer T
  >
    ? T
    : never)['height'],
  number
>

export function useCardActivityFeedBottomSheetAnimatedHeight(
  maxHeight: number,
  minHeight: number,
  animatedIndex: SharedValue<number>,
  otherStyles?: ViewStyle,
  heightOffset = 0,
) {
  const isCardActivityFeedBottomSheetFullHeight = useIsCardActivityFeedBottomSheetFullHeight()

  return useAnimatedStyle(() => ({
    height: isCardActivityFeedBottomSheetFullHeight
      ? '100%'
      : (maxHeight - minHeight) * animatedIndex.value + minHeight - heightOffset,
    ...otherStyles,
  }))
}

/** @todo make these calculations responsive to window resize events on web */
export function useCardActivityFeedBottomSheetHeightStore(
  /** this may vary between envs / screens, etc. android vs ios, prod vs storybook, etc. */
  bottomNavBarHeight: number,
) {
  // the height of the component/content that, when the bottom sheet is at its minimum height, is shown above the bottom sheet
  const [heightAbove, setHeightAbove] = useState(0)

  const { top: topNotchHeight, bottom: bottomNotchHeight } = useSafeAreaInsets()
  const { height: windowHeight } = useWindowDimensions()
  const { height: nativeSafeAreaFrameHeight } = useSafeAreaFrame()
  const safeAreaFrameHeight = Platform.select({
    web: windowHeight - topNotchHeight - bottomNotchHeight,
    native: nativeSafeAreaFrameHeight,
    default: nativeSafeAreaFrameHeight,
  })

  /** the highest snap point of the bottom sheet (measured in pixels from the bottom of the window) */
  const maxHeight = safeAreaFrameHeight - bottomNavBarHeight - bottomNotchHeight

  /** the lowest snap point of the bottom sheet (measured in pixels from the bottom of the window) */
  const minHeight = Math.max(
    maxHeight - heightAbove - Platform.select({ android: 8, ios: 32, default: 0 }),
    windowHeight * 0.22,
  )

  /** 0 when at the minHeight, 1 when at the maxHeight */
  const animatedIndex = useSharedValue(0)

  const animatedStyles = useCardActivityFeedBottomSheetAnimatedHeight(
    maxHeight,
    minHeight,
    animatedIndex,
  )

  return {
    animatedIndex,
    animatedStyles,
    minHeight,
    maxHeight,
    setHeightAbove,
    handleHeight,
  } as const
}

export type CardActivityFeedBottomSheetHeightStore = ReturnType<
  typeof useCardActivityFeedBottomSheetHeightStore
>
